import { useState, useEffect, useRef } from "react";
import {
  Stack,
  Typography,
  Box,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  ListItemIcon,
} from "@mui/material";
import { DotIcon, TriangleIcon } from "../../images";
import { AccountPlan, SubTopic, Topic } from "../../../types";

const handleScrollToTop = (
  id: string,
  isScrolled: boolean,
  isPublicPage: boolean | undefined,
  isDemo: boolean,
) => {
  const el = document.getElementById(id);
  if (el) {
    const extra = isDemo ? 36 : 0;
    const y =
      el.getBoundingClientRect().top +
      window.scrollY -
      (isPublicPage
        ? isScrolled
          ? 152
          : 348
        : isScrolled
          ? 152 + extra
          : 348 + extra);
    window.scrollTo({ top: y, behavior: "smooth" });
  }
};

const scrollThreshold = 54;

export const NavigationBlock = ({
  isDemo,
  accountPlan,
  isPublic,
}: {
  isDemo: boolean;
  accountPlan?: AccountPlan;
  isPublic?: boolean;
}) => {
  const [selected, setSelected] = useState<string>("");
  const [openTopicId, setOpenTopicId] = useState<string | null>(null);
  const [openSubTopics, setOpenSubTopics] = useState<Record<string, boolean>>(
    {},
  );
  const [isSticky, setIsSticky] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      const delta = e.deltaY;
      const up = delta < 0;
      const scrollTop = el.scrollTop;
      const scrollHeight = el.scrollHeight;
      const offsetHeight = el.offsetHeight;

      const atTop = scrollTop === 0;
      const atBottom = scrollTop + offsetHeight >= scrollHeight;

      if ((up && atTop) || (!up && atBottom)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    el.addEventListener("wheel", handleWheel, { passive: false });

    return () => el.removeEventListener("wheel", handleWheel);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > scrollThreshold) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleTopic = (topicId: string) => {
    setOpenTopicId((prev) => (prev === topicId ? null : topicId));
    setOpenSubTopics({});
  };

  const toggleSubTopic = (subId: string) => {
    setOpenSubTopics((prev) => ({
      ...prev,
      [subId]: !prev[subId],
    }));
  };

  return (
    <Box minWidth="260px">
      <Box
        ref={scrollRef}
        sx={{
          width: "260px",
          borderRight: "1px solid #ddd",
          position: isSticky ? "sticky" : "relative",
          top: isSticky ? `${isPublic ? 146 : isDemo ? 178 : 146}px` : "auto",
          height: isSticky
            ? `calc(100vh - ${isDemo ? 178 : 146}px)`
            : "calc(100vh - 154px)",
          overflowY: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Stack>
          {accountPlan?.topics.map((topic) => (
            <NavTopic
              isDemo={isDemo}
              isSticky={isSticky}
              selected={selected}
              setSelected={setSelected}
              key={topic.title}
              topic={topic}
              isOpen={openTopicId === topic.id}
              onToggle={() => toggleTopic(topic.id)}
              openSubTopics={openSubTopics}
              toggleSubTopic={toggleSubTopic}
              isPublic={isPublic}
            />
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

const NavTopic = ({
  topic,
  isOpen,
  onToggle,
  openSubTopics,
  toggleSubTopic,
  selected,
  setSelected,
  isSticky,
  isDemo,
  isPublic,
}: {
  topic: Topic;
  isOpen: boolean;
  onToggle: () => void;
  openSubTopics: Record<string, boolean>;
  toggleSubTopic: (id: string) => void;
  selected: string;
  setSelected: (selected: string) => void;
  isSticky: boolean;
  isDemo: boolean;
  isPublic?: boolean;
}) => {
  return (
    <Box>
      <ListItemButton
        onClick={() => {
          onToggle();

          if (topic.subTopics.length === 0) {
            setSelected(topic.id);
            handleScrollToTop(topic.id, isSticky, isPublic, isDemo);
          }
        }}
        sx={{
          gap: "12px",
          justifyContent: "flex-start",
          backgroundColor: selected === topic.id ? "#F1F6FF" : "transparent",
          padding: 0,
          paddingLeft: "16px",
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            width: "5px",
            height: "100%",
            backgroundColor: selected === topic.id ? "#0860A8" : "transparent",
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: "10px" }}>
          {topic.subTopics.length > 0 &&
            (isOpen ? <TriangleIcon /> : <TriangleIcon rotate="-90" />)}
        </ListItemIcon>
        <ListItemText
          onClick={(e) => {
            e.stopPropagation();
            if (!isOpen && selected !== topic.id) {
              onToggle();
            }

            if (selected === topic.id) {
              onToggle();
            }

            handleScrollToTop(topic.id, isSticky, isPublic, isDemo);
            setSelected(topic.id);
          }}
          sx={{ padding: "10px 24px 10px 0", margin: 0 }}
          primary={
            <Typography fontWeight="bold" fontSize="14px" lineHeight="20px">
              {topic.title}
            </Typography>
          }
        />
      </ListItemButton>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <Box>
          {topic.subTopics.map((sub) => (
            <NavSubTopic
              isDemo={isDemo}
              isSticky={isSticky}
              selected={selected}
              setSelected={setSelected}
              key={sub.id}
              subTopic={sub}
              isOpen={openSubTopics[sub.id]}
              onToggle={() => toggleSubTopic(sub.id)}
              isPublic={isPublic}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const NavSubTopic = ({
  subTopic,
  isOpen,
  onToggle,
  selected,
  setSelected,
  isSticky,
  isDemo,
  isPublic,
}: {
  subTopic: SubTopic;
  isOpen: boolean;
  isSticky: boolean;
  isDemo: boolean;
  isPublic?: boolean;
  onToggle: () => void;
  selected: string;
  setSelected: (selected: string) => void;
}) => {
  return (
    <Box>
      <ListItemButton
        onClick={() => {
          onToggle();
          if (subTopic.article.length === 0) {
            setSelected(subTopic.id);
            handleScrollToTop(subTopic.id, isSticky, isPublic, isDemo);
          }
        }}
        sx={{
          gap: "12px",
          justifyContent: "flex-start",
          backgroundColor: selected === subTopic.id ? "#F1F6FF" : "transparent",
          position: "relative",
          padding: 0,
          paddingLeft: "32px",
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            width: "5px",
            height: "100%",
            backgroundColor:
              selected === subTopic.id ? "#0860A8" : "transparent",
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: "10px" }}>
          {subTopic.article.length > 0 &&
            subTopic.article.some(({ title }) => title) &&
            (isOpen ? <TriangleIcon /> : <TriangleIcon rotate="-90" />)}
        </ListItemIcon>
        <ListItemText
          sx={{ padding: "10px 24px 10px 0", margin: 0 }}
          onClick={(e) => {
            e.stopPropagation();

            e.stopPropagation();
            if (!isOpen && selected !== subTopic.id) {
              onToggle();
            }

            if (selected === subTopic.id) {
              onToggle();
            }

            setSelected(subTopic.id);
            handleScrollToTop(subTopic.id, isSticky, isPublic, isDemo);
          }}
          primary={
            <Typography fontWeight={500} fontSize="14px" lineHeight="20px">
              {subTopic.title}
            </Typography>
          }
        />
      </ListItemButton>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List dense disablePadding>
          {subTopic.article.map((article) => {
            if (!article.title) return null;
            return (
              <ListItemButton
                onClick={() => {
                  handleScrollToTop(article.id, isSticky, isPublic, isDemo);
                  setSelected(article.id);
                }}
                key={article.id}
                sx={{
                  padding: 0,
                  paddingLeft: "48px",
                  gap: "4px",
                  backgroundColor:
                    selected === article.id ? "#F1F6FF" : "transparent",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: "5px",
                    height: "100%",
                    backgroundColor:
                      selected === article.id ? "#0860A8" : "transparent",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: "fit-content" }}>
                  <DotIcon height="12px" width="12px" color="#333333" />
                </ListItemIcon>
                <ListItemText
                  sx={{ padding: "10px 24px 10px 0", margin: 0 }}
                  primary={
                    <Typography
                      fontWeight={400}
                      fontSize="14px"
                      lineHeight="20px"
                    >
                      {article.title}
                    </Typography>
                  }
                />
              </ListItemButton>
            );
          })}
        </List>
      </Collapse>
    </Box>
  );
};
